import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader } from "lucide-react";

import { getReport } from "../api/perception";
import Chip from "./Chip";

const scoreTextColor = (score) => {
  if (score >= 8) return "text-emerald-300";
  if (score >= 6) return "text-emerald-400";
  if (score >= 5) return "text-yellow-400";
  return "text-orange-400";
};

const scoreBgColor = (score) => {
  if (score >= 8) return "bg-emerald-700";
  if (score >= 6) return "bg-emerald-500";
  if (score >= 5) return "bg-yellow-500";
  return "bg-orange-500";
};

const sentimentChipColor = (sentiment) => {
  if (sentiment === "positive") return "emerald";
  if (sentiment === "negative") return "red";
  return "neutral";
};

const PerceptionReport = ({ reportId, onReset }) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("accessToken"));
  const [retryCount, setRetryCount] = useState(0);

  // Track the report alongside a fetchKey (reportId + retryCount) so loading/error
  // can be derived: a stale entry (different fetchKey) means we're still loading.
  const [data, setData] = useState({ fetchKey: null, report: null, error: null });

  useEffect(() => {
    const handleAuthChange = () => {
      const loggedIn = !!localStorage.getItem("accessToken");
      setIsLoggedIn(loggedIn);
      if (loggedIn) setRetryCount((c) => c + 1);
    };
    window.addEventListener("authChange", handleAuthChange);
    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  const fetchKey = `${reportId}-${retryCount}`;
  useEffect(() => {
    const controller = new AbortController();
    const key = `${reportId}-${retryCount}`;
    getReport(reportId, { signal: controller.signal })
      .then((res) => setData({ fetchKey: key, report: res.data, error: null }))
      .catch(() => {
        if (controller.signal.aborted) return;
        setData({ fetchKey: key, report: null, error: "Failed to fetch report." });
      });
    return () => controller.abort();
  }, [reportId, retryCount]);

  const loading = data.fetchKey !== fetchKey;
  const report = loading ? null : data.report;
  const error = loading ? null : data.error;

  if (error) {
    return (
      <div className="text-center py-16">
        {isLoggedIn && <p className="text-red-400 mb-4">{error}</p>}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {isLoggedIn ? (
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary text-on-primary rounded-xl font-bold hover:opacity-90"
            >
              Retry
            </button>
          ) : (
            <button
              onClick={() => navigate("?auth=login")}
              className="px-6 py-2 bg-primary text-on-primary rounded-xl font-bold hover:opacity-90"
            >
              Log in to view your report
            </button>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (report.status === "failed") {
    return (
      <div className="text-center py-16">
        <p className="text-red-400 mb-4">Report generation failed. Please try again.</p>
        <button
          onClick={onReset}
          className="px-6 py-2 bg-primary text-on-primary rounded-xl font-bold hover:opacity-90"
        >
          Retry
        </button>
      </div>
    );
  }

  const profile = report.business_profile ?? {};
  const scores = report.attribute_scores ?? [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="font-headline text-2xl font-bold">
          {profile.business_name} — AI Perception
        </h2>
        <p className="text-on-surface-variant text-sm mt-1">
          {profile.city} · {report.engines_used.join(", ")} · {scores.length} attributes scored
        </p>
      </div>

      {/* Summary chips */}
      {(report.strengths.length > 0 ||
        report.weaknesses.length > 0 ||
        report.blind_spots.length > 0 ||
        report.audience_fit.length > 0) && (
        <div className="space-y-3">
          {report.strengths.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider pt-0.5">
                Strengths
              </span>
              {report.strengths.map((s) => (
                <Chip key={s} color="emerald">
                  {s}
                </Chip>
              ))}
            </div>
          )}
          {report.weaknesses.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider pt-0.5">
                Weaknesses
              </span>
              {report.weaknesses.map((s) => (
                <Chip key={s} color="orange">
                  {s}
                </Chip>
              ))}
            </div>
          )}
          {report.blind_spots.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider pt-0.5">
                Blind spots
              </span>
              {report.blind_spots.map((s) => (
                <Chip key={s} color="red">
                  {s}
                </Chip>
              ))}
            </div>
          )}
          {report.audience_fit.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider pt-0.5">
                AIs think you&apos;re for
              </span>
              {report.audience_fit.map((s) => (
                <Chip key={s} color="blue">
                  {s}
                </Chip>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Attribute scores */}
      <div>
        <h3 className="font-headline text-lg font-bold mb-3">Attribute Scores</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {scores
            .slice()
            .sort((a, b) => b.score - a.score)
            .map((s) => (
              <div
                key={s.attribute_id}
                className="bg-surface-container-low rounded-xl p-5 border border-surface-container-high"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-base">{s.label}</span>
                    <div className="mt-2 h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${scoreBgColor(s.score)} transition-all`}
                        style={{ width: `${s.score * 10}%` }}
                      />
                    </div>
                  </div>
                  <div
                    className={`text-2xl font-extrabold font-headline shrink-0 ${scoreTextColor(s.score)}`}
                  >
                    {s.score.toFixed(1)}
                  </div>
                </div>
                <div className="mt-3 flex gap-2 text-sm text-on-surface-variant flex-wrap items-center">
                  <Chip color={sentimentChipColor(s.sentiment)}>{s.sentiment}</Chip>
                  <span>Mentioned {Math.round(s.mention_rate * 100)}% of responses</span>
                  {s.is_blind_spot && <Chip color="red">Blind spot</Chip>}
                  {!s.is_blind_spot && s.is_weakness && <Chip color="orange">Needs attention</Chip>}
                </div>
                {s.evidence?.length > 0 && (
                  <p className="mt-2 text-xs text-on-surface-variant italic truncate">
                    &ldquo;{s.evidence[0]}&rdquo;
                  </p>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Fixes */}
      {report.fixes.length > 0 && (
        <div>
          <h3 className="font-headline text-lg font-bold mb-3">
            Recommended Fixes
            {report.fixes_degraded && (
              <span className="text-on-surface-variant font-normal text-sm ml-2">
                — template-based (AI personalization unavailable this run)
              </span>
            )}
          </h3>
          <div className="space-y-3">
            {report.fixes
              .slice()
              .sort((a, b) => a.priority - b.priority)
              .map((fix) => (
                <div
                  key={fix.id}
                  className="bg-surface-container-low rounded-xl p-5 border border-surface-container-high"
                >
                  <div className="font-semibold text-base mb-2">{fix.title}</div>
                  {fix.rationale && (
                    <p className="text-sm text-on-surface-variant mb-2">{fix.rationale}</p>
                  )}
                  <ol className="list-decimal list-inside space-y-1 text-sm text-on-surface-variant">
                    {fix.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerceptionReport;
